import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  ChevronRight,
  Image as ImageIcon,
  Inbox,
  PenLine,
  Plus,
  RefreshCw,
  TrendingUp,
  Trophy,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { RequestStatus, RequestType } from '@geoic/shared';
import { api, type ListResult } from '@/lib/api-client.js';
import { cn } from '@/lib/cn.js';
import { dateOnly, price } from '@/lib/format.js';
import { queryKeys } from '@/lib/query-keys.js';
import { Card, CardBody } from '@/components/ui/Card.js';
import { RequestStatusBadge } from '@/components/ui/Badge.js';
import { useAuth } from '@/features/auth/auth-context.js';

type RequestRow = {
  _id: string;
  requestNumber: string;
  type: RequestType;
  contact: { name: string };
  status: RequestStatus;
  estimatedAmount: number | null;
  createdAt: string;
};

type StatsPeriod = 'today' | 'month' | 'year';

type Stats = {
  period: StatsPeriod;
  totals: { total: number; amount: number; newCount: number; inProgress: number; won: number };
  delta: { total: number | null; newCount: number; won: number; amount: number | null };
  chart: Array<{ bucket: string; count: number }>;
  byType: Array<{ type: RequestType; count: number; percent: number }>;
  topProducts: Array<{ title: string; count: number }>;
  attention: { staleNew: number; telegramFailed: number };
};

export const TYPE_LABELS: Record<RequestType, string> = {
  product: 'Mahsulot',
  cart: 'Ariza ro‘yxati',
  drone_show: 'Dron-shou',
  repair: 'Ta’mirlash',
  course: 'Kurs',
  service: 'Xizmat',
  general: 'Umumiy murojaat',
};

/** Maketdagi ulush chiziqlari ranglari (`D1`, ARIZA TURLARI). */
const TYPE_COLORS: Record<RequestType, string> = {
  product: 'bg-brand-600',
  cart: 'bg-brand-500',
  general: 'bg-n-400',
  drone_show: 'bg-accent-600',
  repair: 'bg-warning-600',
  course: 'bg-success-600',
  service: 'bg-info-600',
};

const PERIODS: Array<{ value: StatsPeriod; label: string }> = [
  { value: 'today', label: 'Bugun' },
  { value: 'month', label: '30 kun' },
  { value: 'year', label: 'Yil' },
];

/**
 * Boshqaruv paneli — MAKETDAN (`D1 Login va boshqaruv paneli.dc.html`).
 *
 * To'rtta ko'rsatkich, arizalar dinamikasi grafigi, turlar ulushi,
 * oxirgi arizalar jadvali va o'ng ustunda «Diqqat talab qiladi»,
 * tez havolalar hamda eng ko'p so'ralgan mahsulotlar.
 *
 * Barcha sonlar BITTA endpointdan keladi (`GET /requests/stats`):
 * ilgari har bir ko'rsatkich uchun alohida ro'yxat so'rovi ketardi.
 */
export function DashboardPage() {
  const { can } = useAuth();
  const canSeeRequests = can('superadmin', 'admin', 'sales');
  const [period, setPeriod] = useState<StatsPeriod>('month');

  const { data: stats } = useQuery({
    queryKey: queryKeys.requests.stats(period),
    queryFn: () => api.get<Stats>('/requests/stats', { period }),
    enabled: canSeeRequests,
  });

  const { data: recent, isLoading } = useQuery({
    queryKey: queryKeys.requests.list({ limit: 5 }),
    queryFn: () => api.get<ListResult<RequestRow>>('/requests', { page: 1, limit: 5 }),
    enabled: canSeeRequests,
  });

  if (!canSeeRequests) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-n-600">
            Sizning rolingizda ko‘rsatiladigan ko‘rsatkichlar yo‘q. Chap menyudan kerakli bo‘limni
            tanlang.
          </p>
        </CardBody>
      </Card>
    );
  }

  const totals = stats?.totals;
  const delta = stats?.delta;

  return (
    <div className="flex flex-col gap-[22px]">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <h1 className="text-[28px] font-semibold tracking-[-0.02em] text-n-900">
            Boshqaruv paneli
          </h1>
          <p className="mt-1.5 text-sm text-n-600">Bugun {dateOnly(new Date())}</p>
        </div>

        {/* Davr tanlagichi — maketda segmentli tugmalar */}
        <div className="flex border border-n-200 bg-white">
          {PERIODS.map((item) => (
            <button
              key={item.value}
              type="button"
              aria-pressed={period === item.value}
              onClick={() => setPeriod(item.value)}
              className={cn(
                'px-4 py-2.5 text-[13.5px] font-semibold',
                period === item.value ? 'bg-brand-600 text-white' : 'text-n-800 hover:bg-n-50',
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          label="Yangi arizalar"
          value={totals?.newCount}
          icon={Inbox}
          delta={formatDiff(delta?.newCount)}
          deltaUp={(delta?.newCount ?? 0) >= 0}
          note="oldingi davrga nisbatan"
        />
        <Kpi
          label="Jami arizalar"
          value={totals?.total}
          icon={TrendingUp}
          delta={formatPercent(delta?.total)}
          deltaUp={(delta?.total ?? 0) >= 0}
          note="oldingi davrga nisbatan"
        />
        <Kpi
          label="Yutilgan arizalar"
          value={totals?.won}
          icon={Trophy}
          delta={formatDiff(delta?.won)}
          deltaUp={(delta?.won ?? 0) >= 0}
          note="oldingi davrga nisbatan"
        />
        <Kpi
          label="Umumiy summa"
          value={totals === undefined ? undefined : price(totals.amount)}
          icon={Wallet}
          delta={formatPercent(delta?.amount)}
          deltaUp={(delta?.amount ?? 0) >= 0}
          note="oldingi davrga nisbatan"
        />
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-[1.5fr_1fr]">
        <Card>
          <div className="flex items-center justify-between gap-4 border-b border-n-100 px-5 py-[18px]">
            <span className="text-base font-semibold text-n-900">Arizalar dinamikasi</span>
            <span className="font-mono text-xs text-n-600">
              {PERIODS.find((item) => item.value === period)?.label}
            </span>
          </div>
          <CardBody>
            <Chart data={stats?.chart ?? []} />
          </CardBody>
        </Card>

        <Card>
          <div className="border-b border-n-100 px-5 py-[18px] text-base font-semibold text-n-900">
            Ariza turlari
          </div>
          <CardBody className="flex flex-col gap-3.5">
            {(stats?.byType ?? []).length === 0 ? (
              <p className="text-sm text-n-600">Bu davrda ariza yo‘q.</p>
            ) : (
              stats?.byType.map((row) => (
                <div key={row.type} className="flex flex-col gap-1.5">
                  <div className="flex justify-between gap-3 text-[13.5px]">
                    <span className="text-n-800">{TYPE_LABELS[row.type]}</span>
                    <span className="font-mono font-semibold tabular text-n-900">
                      {row.percent}%
                    </span>
                  </div>
                  <div className="h-2 bg-n-100">
                    <div
                      className={cn('h-full', TYPE_COLORS[row.type])}
                      style={{ width: `${row.percent}%` }}
                    />
                  </div>
                </div>
              ))
            )}
          </CardBody>
        </Card>
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-[1.5fr_1fr]">
        <Card>
          <div className="flex items-center justify-between gap-4 border-b border-n-100 px-5 py-[18px]">
            <span className="text-base font-semibold text-n-900">Oxirgi arizalar</span>
            <Link
              to="/requests"
              className="flex items-center gap-1 text-[13.5px] font-semibold text-brand-600 hover:text-brand-700"
            >
              Barchasi
              <ArrowRight className="size-4" aria-hidden />
            </Link>
          </div>

          {/*
            `DataTable` EMAS — maketda bu ixcham jadval: ustunlari
            aniq, saralash va bo'sh holat tugmalari yo'q.
          */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b border-n-200 bg-n-50 text-left">
                  <th className="px-5 py-2.5 font-semibold whitespace-nowrap">Raqam</th>
                  <th className="px-3.5 py-2.5 font-semibold">Mijoz</th>
                  <th className="px-3.5 py-2.5 font-semibold">Turi</th>
                  <th className="px-3.5 py-2.5 text-right font-semibold whitespace-nowrap">
                    Summa
                  </th>
                  <th className="px-5 py-2.5 font-semibold">Holat</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={5} className="px-5 py-8 text-center text-sm text-n-600">
                      Yuklanmoqda…
                    </td>
                  </tr>
                )}
                {!isLoading && (recent?.data ?? []).length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-8 text-center text-sm text-n-600">
                      Hozircha ariza yo‘q
                    </td>
                  </tr>
                )}
                {(recent?.data ?? []).map((row) => (
                  <tr key={row._id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                    <td className="px-5 py-3 font-mono font-medium whitespace-nowrap text-brand-600">
                      <Link to={`/requests?search=${row.requestNumber}`}>{row.requestNumber}</Link>
                    </td>
                    <td className="px-3.5 py-3 font-medium">{row.contact.name}</td>
                    <td className="px-3.5 py-3 text-n-600">{TYPE_LABELS[row.type]}</td>
                    <td className="px-3.5 py-3 text-right font-mono tabular whitespace-nowrap">
                      {row.estimatedAmount === null ? '—' : price(row.estimatedAmount)}
                    </td>
                    <td className="px-5 py-3">
                      <RequestStatusBadge status={row.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="flex flex-col gap-4">
          <Attention attention={stats?.attention} />

          <Card>
            <div className="border-b border-n-100 px-5 py-4 text-[15px] font-semibold text-n-900">
              Tez havolalar
            </div>
            <CardBody className="flex flex-col gap-2">
              <QuickLink to="/products/new" icon={Plus} label="Mahsulot qo‘shish" />
              <QuickLink to="/news/new" icon={PenLine} label="Yangilik yozish" />
              <QuickLink to="/prices" icon={RefreshCw} label="Narxlarni yangilash" />
              <QuickLink to="/media" icon={ImageIcon} label="Media yuklash" />
            </CardBody>
          </Card>

          <Card>
            <div className="border-b border-n-100 px-5 py-4 text-[15px] font-semibold text-n-900">
              Eng ko‘p so‘ralgan mahsulotlar
            </div>
            {(stats?.topProducts ?? []).length === 0 ? (
              <CardBody>
                <p className="text-sm text-n-600">Bu davrda mahsulot arizasi bo‘lmagan.</p>
              </CardBody>
            ) : (
              <ul>
                {stats?.topProducts.map((item, index) => (
                  <li
                    key={item.title}
                    className="flex items-center gap-3 border-b border-n-100 px-5 py-2.5 text-[13.5px] last:border-b-0"
                  >
                    <span className="w-4 font-mono text-xs text-n-600">{index + 1}</span>
                    <span className="flex-1 text-n-900">{item.title}</span>
                    <span className="font-mono text-[13px] tabular text-n-600">{item.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

/** Maketdagi ko'rsatkich kartochkasi: yorliq, mono son va o'zgarish. */
function Kpi({
  label,
  value,
  icon: Icon,
  delta,
  deltaUp,
  note,
}: {
  label: string;
  value: number | string | undefined;
  icon: LucideIcon;
  delta: string | null;
  deltaUp: boolean;
  note: string;
}) {
  return (
    <Card className="flex flex-col gap-2.5 p-5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[13px] text-n-600">{label}</span>
        <Icon className="size-[17px] text-n-400" aria-hidden />
      </div>
      <span className="font-mono text-[30px] leading-none font-semibold tracking-[-0.03em] tabular text-n-900">
        {value === undefined ? '—' : value}
      </span>
      <span className="flex items-center gap-2 text-[12.5px]">
        {delta && (
          <span
            className={cn('font-mono font-medium', deltaUp ? 'text-success-600' : 'text-error-600')}
          >
            {delta}
          </span>
        )}
        <span className="text-n-600">{note}</span>
      </span>
    </Card>
  );
}

/**
 * Ustunli grafik — CSS bilan.
 *
 * Kutubxona ATAYLAB qo'shilmadi: 30 ta to'g'ri to'rtburchak uchun
 * chizish kutubxonasi admin paketiga o'nlab kilobayt qo'shardi.
 */
function Chart({ data }: { data: Array<{ bucket: string; count: number }> }) {
  const max = Math.max(1, ...data.map((point) => point.count));

  return (
    <div>
      <div className="flex h-[190px] items-end gap-1">
        {data.map((point) => (
          <span
            key={point.bucket}
            title={`${point.bucket}: ${point.count}`}
            className={cn(
              'min-h-[3px] flex-1 hover:bg-brand-600',
              point.count >= max * 0.8 ? 'bg-brand-600' : 'bg-brand-100',
            )}
            style={{ height: `${Math.round((point.count / max) * 100)}%` }}
          />
        ))}
      </div>
      {data.length > 0 && (
        <div className="mt-2.5 flex justify-between font-mono text-[11px] tabular text-n-600">
          <span>{data[0]?.bucket}</span>
          <span>{data[data.length - 1]?.bucket}</span>
        </div>
      )}
    </div>
  );
}

/** «Diqqat talab qiladi» — maketda sariq chegarali quti. */
function Attention({ attention }: { attention: Stats['attention'] | undefined }) {
  const rows: Array<{ text: string; to: string }> = [];
  if (attention?.staleNew) {
    rows.push({
      text: `${attention.staleNew} ta ariza 2 kundan beri javobsiz`,
      to: '/requests?status=new',
    });
  }
  if (attention?.telegramFailed) {
    rows.push({
      text: `${attention.telegramFailed} ta xabar Telegramga yuborilmadi`,
      to: '/settings',
    });
  }
  if (rows.length === 0) return null;

  return (
    <section className="border border-warning-600 bg-warning-50">
      <div className="flex items-center gap-2.5 border-b border-warning-200 px-5 py-4">
        <AlertTriangle className="size-[18px] text-warning-600" aria-hidden />
        <span className="text-[15px] font-semibold text-warning-600">Diqqat talab qiladi</span>
      </div>
      <div className="py-2">
        {rows.map((row) => (
          <Link
            key={row.to}
            to={row.to}
            className="flex items-center gap-3 px-5 py-2.5 text-[13.5px] text-n-800 hover:bg-warning-50"
          >
            <span aria-hidden className="size-1.5 shrink-0 bg-warning-600" />
            <span className="flex-1">{row.text}</span>
            <ChevronRight className="size-[15px] shrink-0 text-warning-600" aria-hidden />
          </Link>
        ))}
      </div>
    </section>
  );
}

function QuickLink({ to, icon: Icon, label }: { to: string; icon: LucideIcon; label: string }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 border border-n-200 px-3.5 py-2.5 text-[13.5px] font-medium text-n-900 hover:border-brand-600 hover:bg-brand-50"
    >
      <Icon className="size-4 text-brand-600" aria-hidden />
      {label}
    </Link>
  );
}

/** «+4» / «−2» — nol bo'lsa umuman ko'rsatilmaydi. */
function formatDiff(value: number | undefined): string | null {
  if (value === undefined || value === 0) return null;
  return value > 0 ? `+${value}` : `−${Math.abs(value)}`;
}

/** «+18%» — oldingi davrda ariza bo'lmasa foiz ma'nosiz va yashiriladi. */
function formatPercent(value: number | null | undefined): string | null {
  if (value === null || value === undefined || value === 0) return null;
  return value > 0 ? `+${value}%` : `−${Math.abs(value)}%`;
}
